import clsx from "clsx";
import { Icon, Text, View } from ".";

type SettingsListItemProps = {
  className?: string;
  icon?: string;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned content (typically actions). If omitted, `children` is used. */
  right?: React.ReactNode;
  /** Full-width content rendered below the main row (e.g. alerts). */
  below?: React.ReactNode;
  /** Back-compat: if `right` is not provided, children renders as the right section. */
  children?: React.ReactNode;
};

export const SettingsListItem = ({ children, className, icon, title, subtitle, right, below }: SettingsListItemProps) => {
  const titleNode =
    typeof title === "string" ? (
      <Text
        variant="body"
        className="font-semibold"
      >
        {title}
      </Text>
    ) : (
      title
    );

  const subtitleNode =
    typeof subtitle === "string" ? (
      <Text
        variant="muted"
        className="text-sm text-gray-500 dark:text-gray-400"
      >
        {subtitle}
      </Text>
    ) : (
      subtitle
    );

  return (
    <View className={clsx("px-4 py-3", "hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors", "flex flex-col gap-3", className)}>
      <View className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <View className="flex items-center space-x-3 min-w-0">
          {icon && (
            <View className="flex-shrink-0 h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              {icon.length === 1 ? (
                <Text className="text-gray-600 dark:text-gray-300 font-medium text-sm">{icon}</Text>
              ) : (
                <Icon
                  name={icon}
                  size={14}
                  className="text-gray-600 dark:text-gray-300"
                />
              )}
            </View>
          )}

          <View className="flex flex-col min-w-0">
            {titleNode}
            {subtitleNode}
          </View>
        </View>

        <View className="flex flex-row flex-wrap gap-2">{right ?? children}</View>
      </View>

      {below}
    </View>
  );
};
